<template>
  <div class="layout-row items-center flex-grow justify-center md:justify-between max-w-[1044px]">
    <div
      v-if="ssoError"
      class="px-md absolute left-0 right-0 top-sm"
    >
      <OrNotification
        class="sm:max-w-[768px] mx-auto"
        variant="error"
        @close="dismissSso"
      >
        {{ ssoError }}
      </OrNotification>
    </div>
    <div
      v-if="recoveryError"
      class="px-md absolute left-0 right-0 top-sm"
    >
      <OrNotification
        v-if="recoveryError"
        class="sm:max-w-[768px] mx-auto"
        variant="error"
        @close="recoveryError=''"
      >
        {{ recoveryError }}
      </OrNotification>
    </div>
    <div class="layout-row items-center flex-grow justify-center md:justify-between max-w-[1044px]">
      <div
        v-if="isDesktop"
        class="flex h-[504px] w-[492px] justify-center items-center"
      >
        <OrAnimationLogo />
      </div>
      <MFAForm
        v-if="twoFactorCheck"
        v-model:verification-code="verificationCode"
        :username="username!"
        :error="error"
        :is-loading="loading"
        @submit="submit"
        @paste-from-buffer="pasteMfaFromBuffer"
        @logout="logout"
        @toogle-open-recovery2fa="toogleOpenRecovery2fa(true)"
      />
      <div
        v-else
        class="form layout-column p-md sm:p-0 gap-xl sm:w-[343px] md:w-[352px]"
      >
        <OrLogo
          class="self-center md:hidden mb-md"
        />
        <div class=" layout-column gap-md items-center md:items-stretch">
          <h2 class="typography-display-small dark:text-on-background-dark text-center">
            {{ $t('session_expire.Your session has expired') }}
          </h2>
          <div
            v-if="!userIdentityProvider"
            class="typography-body-2-regular dark:text-on-background-dark text-center md:text-start"
          >
            {{ $t(`session_expire.${titleText}`) }}
          </div>
          <span
            v-else
            class="typography-body-2-regular dark:text-on-background-dark"
          >
            {{ $t(`session_expire.${titleText['Due to inactivity']}`) }}
            <span class="typography-body-2-bold">
              {{ titleText['Provider name'] }}
            </span>
            {{ $t(`session_expire.${titleText['user or sign out']}`) }}
          </span>
        </div>
        <template v-if="!userIdentityProvider">
          <div class="layout-column gap-md">
            <OrInput
              :model-value="username"
              :label="$t('session_expire.Email')"
              readonly
            />

            <OrInput
              ref="passwordInput"
              v-model="password"
              class="password"
              type="password"
              :placeholder="$t('session_expire.Enter password')"
              :label="$t('session_expire.Password')"
              :error="error"
              :disabled="loading"
              @keyup.enter.native="submit"
            />
            <div class="layout-row justify-between gap-md">
              <OrCheckbox
                :model-value="keepLoggedIn"
                class="keep-logged-in grey"
                :disabled="loading"
                @update:modelValue="updateKeepLoggedIn"
              >
                {{ $t('login.login.Keep me signed in') }}
              </OrCheckbox>
              <OrButton
                variant="link"
                @click.prevent="showForgotPopup"
              >
                {{ $t('login.login.forgotPassword') }}
              </OrButton>
            </div>
          </div>
        </template>
        <div class="button-wrapper layout-column gap-md">
          <OrButton
            v-if="!userIdentityProvider"
            :loading="loading"
            class="sign-in"
            @click="submit"
          >
            {{ $t('session_expire.Sign in') }}
          </OrButton>
          <OrButton
            v-else
            :loading="loading"
            class="sign-in"
            @click="loginWithProvider"
          >
            {{ $t('session_expire.Sign in') }}
          </OrButton>
          <GoogleLogin
            v-if="googleLogin && !twoFactorCheck"
            v-model:model-value="googleCode"
            :verification-code="verificationCode"
            :account-data="{}"
            button-type="outlined"
            :label="$t('login.login.Sign in with Google')"
            :loading="googleLoading"
            :keep-logged-in="keepLoggedIn"
            from-expired
          />
          <PasskeyLogin
            :disabled="googleLoading || loading"
            button-type="outlined"
            :label="translate('login.login.Sign in with Passkey')"
            :keep-logged-in="keepLoggedIn"
            :account-data="{accountId}"
            @error="e => recoveryError=e"
          />
        </div>
        <OrButton
          class="md:self-start"
          variant="link"
          @click="logout"
        >
          {{ $t('login.login.Use different email') }}
        </OrButton>
      </div>

      <MFARecoveryModal
        v-model="recoveryCode"
        :is-open-recovery2fa="isOpenRecovery2fa"
        :is-loading="loading"
        :recovery-error="recoveryError"
        @toogle-open-recovery2fa="toogleOpenRecovery2fa"
        @submit="submit"
      />

      <PasswordRecoveryModal
        ref="resetPasswordModal"
        :redirect-path="redirectPath"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  OrButtonV3 as OrButton,
  OrInputV3 as OrInput,
  OrCheckboxV3 as OrCheckbox,
  OrNotificationV3 as OrNotification,
  useResponsive,
} from '@onereach/ui-components';
import { ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import useI18n from '@/composables/useI18n';

import { Credentials, RequestData } from '../../types/OrSignIn';
import GoogleLogin from '../Auth/GoogleLogin.vue';
import MFAForm from '../Auth/MFAForm.vue';
import MFARecoveryModal from '../Auth/MFARecoveryModal.vue';
import PasskeyLogin from '../Auth/PasskeyLogin.vue';
import PasswordRecoveryModal from '../Auth/PasswordRecoveryModal.vue';
import OrAnimationLogo from '../OrAnimationLogo/OrAnimationLogo.vue';
import OrLogo from '../OrLogo';

const { translate } = useI18n();


const props = defineProps<{
  twoFactorCheck: boolean;
  titleText: string | object;
  error: string | boolean;
  loading: boolean;
  userIdentityProvider?: boolean | string | null;
  username: string | null;
  googleLogin: boolean ;
  ssoError: string | boolean;
  googleLoading: boolean;
  keepLoggedIn: boolean;
  redirectPath: string | null;
}>();

const emit = defineEmits<{
  (e: 'submit', requestData: RequestData, openRecovery: boolean, twoFactorCode: string): void;
  (e: 'logout'): void;
  (e: 'dismissSso'): void;
  (e: 'providerLogin'): void;
  (e: 'resetError'): void;
  (e: 'update:keepLoggedIn', value: boolean): void;
}>();

const googleCode = defineModel<string>('googleCode', { required: true });

const password = ref('');
const verificationCode = ref('');
const recoveryCode = ref('');
const isOpenRecovery2fa = ref(false);
const rememberTwoFactor = ref(false);

const { isDesktop } = useResponsive();

const submit = () => {
  recoveryError.value = '';

  const credentials: Credentials = {
    username: props.username,
    password: password.value,
  };
  const requestData: RequestData = {
    credentials,
    keepLoggedIn: props.keepLoggedIn,
    rememberTwoFactor: rememberTwoFactor.value,
  };
  const twoFactorCode = isOpenRecovery2fa.value ? recoveryCode.value : verificationCode.value;
  emit('submit', requestData, isOpenRecovery2fa.value, twoFactorCode);
};


const logout = () => {
  emit('logout');
};

const dismissSso = () => {
  emit('dismissSso');
};

const recoveryError = ref<string | boolean>('');

const toogleOpenRecovery2fa = (boolean) => {
  isOpenRecovery2fa.value = boolean;
  if (!boolean) {
    recoveryCode.value = '';
    recoveryError.value = '';
  }
};

const resetPasswordModal = ref<InstanceType<typeof PasswordRecoveryModal> | null>(null);

const showForgotPopup = () => {
  resetPasswordModal.value?.open();
};

const loginWithProvider = () => {
  emit('providerLogin');
};

const pasteMfaFromBuffer = async () => {
  try {
    if (!navigator.clipboard) {
      throw new Error('Clipboard API not available');
    }
    const text = await navigator.clipboard.readText();
    verificationCode.value = text;
  } catch (e) {
    console.error('Failed to read clipboard contents: ', e);
  }
};

const updateKeepLoggedIn = (bool: boolean) => {
  emit('update:keepLoggedIn', bool);
};

watch(() => props.error, (value) => {
  if (value && isOpenRecovery2fa.value) {
    if (value === 'Two factor code invalid or expired. Please try again') {
      recoveryError.value = 'Verification code id invalid or expired. Please try again.';
    } else {
      recoveryError.value = value;
    }
    emit('resetError');
  }
});
const router = useRouter();
const { accountId } = router.currentRoute.value.query;
</script>
